import { Button, Spinner } from "@sellar-io/components";
import { ReactElement } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";
import type {
  StockListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../../components/modal/Dialog";
import { CHECKOUT_DELIVERY_BUYER } from "../../../../../../constants/routes";
import {
  useBasketContext,
  useEvents,
  useSellarUser,
} from "../../../../../../hooks";
import { useBuyerProduct } from "../../../../../../hooks/api/v2/product/useBuyerProduct";
import { useStockList } from "../../../../../../hooks/api/v2/stockList/useStockList";
import { useSellarCurrentBusiness } from "../../../../../../hooks/useSellarCurrentBusiness.shared";
import { useMarketStorefrontSessionId } from "../../../../../../hooks/useMarketStorefrontSessionTracking";
import { useSignupContext } from "../../../../../../hooks/useSignupContext.shared";
import { useStorefrontFilterStore } from "../../../../StorefrontFilterStore";
import { generateSignupContinueUrl } from "../../../../../../utils/sellarHelpers";
import { useStockListBasketValue } from "../../../../useStockListBasketValue";
import { useStockListContext } from "../../../../useStockListContext";
import { StorefrontChildProductList } from "../../StorefrontChildProductList/StorefrontChildProductList";
import { StorefrontProductCard } from "../../StorefrontLayout/StorefrontProductCard";

export const StorefrontTableCategoryDialogContent = ({
  onDismiss,
  productId,
  setBestPricePromiseDialogActive,
}: {
  onDismiss: () => void;
  productId: number;
  setBestPricePromiseDialogActive?: (active: boolean) => void;
}): ReactElement => {
  const [searchParams] = useSearchParams();
  const { supplierQuery, unAuthorisedUserPostcode } = useStockListContext();
  const showOutOfStockProducts = useStorefrontFilterStore(
    (state) => state.showOutOfStockProducts,
  );

  const productQuery = useBuyerProduct(productId, {
    enabled: !!productId && !isNaN(productId),
  });

  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");

  const productsListQuery = useStockList(
    {
      ParentId: productId,
      active: true,
      SupplierId: supplierQuery.data?.id ?? 0,
      postcode: unAuthorisedUserPostcode,
      storefrontLinkPassword: storefrontLink ?? undefined,
      showOutOfStockProducts,
    },
    { enabled: !!supplierQuery.data?.id },
  );

  const productItem = productsListQuery.data?.pages[0]?.[0];
  const isLoading =
    productQuery.isLoading ||
    supplierQuery.isLoading ||
    productsListQuery.isLoading;
  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        className={"z-10 pb-0"}
        title={productQuery.data?.name ?? ""}
      />
      <DialogContent
        className={twMerge(
          isLoading
            ? "flex min-h-0 w-full flex-1 items-center justify-center"
            : "mx-auto mb-8 grid w-full content-start gap-y-2 md:grid-cols-12 xl:max-w-[1000px]",
        )}
      >
        {isLoading ? (
          <Spinner size="lg" />
        ) : (
          <>
            {productQuery.data && (
              <>
                <div className="md:col-span-6">
                  <StorefrontProductCard
                    setBestPricePromiseDialogActive={
                      setBestPricePromiseDialogActive
                    }
                    item={productQuery.data}
                    supplier={supplierQuery.data}
                    className="z-0 max-h-[calc(100vh_-_16px_-_var(--nav-height))] md:sticky md:top-[calc(var(--nav-height))] md:col-span-4 md:overflow-auto"
                  />
                </div>
                <StorefrontChildProductList
                  productId={productId}
                  className="md:col-span-6 md:overflow-auto"
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      {supplierQuery.data && !isLoading && (
        <StorefrontTableCategoryDialogContentCta
          productItem={productItem}
          supplier={supplierQuery.data}
          onDismiss={onDismiss}
        />
      )}
    </>
  );
};

const StorefrontTableCategoryDialogContentCta = ({
  productItem,
  supplier,
  onDismiss,
}: {
  productItem: StockListResponseType[number] | undefined;
  supplier: SupplierGetResponseType;
  onDismiss: () => void;
}) => {
  const { track } = useEvents();
  const navigate = useNavigate();
  const { supplierStorefront } = useStockListContext();
  const [{ order }] = useBasketContext();
  const { user } = useSellarUser();
  const { setSignupDialogActive, setBasketTotal } = useSignupContext();
  const [, setOnboardingContinueUrl] = useLocalStorage(
    "onboardingContinueUrl",
    "",
  );
  const [searchParams] = useSearchParams();
  const { sessionId } = useMarketStorefrontSessionId();
  const { business } = useSellarCurrentBusiness();
  const isLoggedIn = !!user && !!business;
  const storefrontLink = searchParams.get("storefrontLink");
  const basketTotal = useStockListBasketValue();
  const hasBasketedItems = !!order?.OrderItems?.length;
  const renderCtaContent = () => {
    if (!isLoggedIn) {
      return (
        <Button
          className={"w-full"}
          onClick={() => {
            const continueSearchParams = generateSignupContinueUrl(
              supplier.id,
              storefrontLink ? `storefrontLink=${storefrontLink}` : "",
            );
            setOnboardingContinueUrl(
              `${continueSearchParams.get("continue")}${
                continueSearchParams.get("queryParameters")
                  ? `?${continueSearchParams.get("queryParameters")}`
                  : ""
              }`,
            );

            setBasketTotal(basketTotal);
            setSignupDialogActive(true);
          }}
        >
          Sign up to order
        </Button>
      );
    }

    if (!productItem?.ProductPriceTier) {
      if (supplierStorefront?.SupplierRetailerConnection) {
        return (
          <Button className={"w-full"} onClick={onDismiss}>
            More from {supplier.Business.name}
          </Button>
        );
      }
      return null;
    }

    return (
      <div className={"flex w-full flex-col gap-2 md:grid md:grid-cols-2"}>
        <Button
          color={"secondary"}
          className={"col-span-1 w-full"}
          disabled={!hasBasketedItems}
          onClick={() => {
            onDismiss();
            // This is the same issue as previously seen when redirecting to checkout from sign up flow.
            // Previously thought it was specifically an error with setting business
            // But seems to be a deeper issue with the loading states in the basket context
            // Have written a tech debt ticket to tackle this at a later date
            setTimeout(() => {
              track("Checkout Started", {
                retailerName: business.name,
                supplierName: supplier.Business.name,
                retailerId: business.Retailer?.id,
                supplierId: supplier.id,
                sessionId,
              });

              return navigate(
                `${CHECKOUT_DELIVERY_BUYER.href({
                  supplierIdOrUrlId: supplier.urlId,
                })}${location.search}`,
              );
            }, 1000);
          }}
        >
          Go to checkout
        </Button>
        <Button className={"col-span-1 w-full"} onClick={onDismiss}>
          Continue ordering
        </Button>
      </div>
    );
  };

  const cta = renderCtaContent();

  if (!cta) return null;
  return <DialogCTA>{cta}</DialogCTA>;
};
